import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { performanceImages, performanceImgPositions } from "../constants";

gsap.registerPlugin(ScrollTrigger);

const imageTitles = {
  p1: "Advanced Diagnostics",
  p2: "Workspace Ready",
  p3: "Performance Testing",
  p4: "Certified Quality",
  p5: "PC Care Expertise",
  p6: "Professionals Team",
  p7: "Trusted Support",
};

const Aslider = () => {
  const sectionRef = useRef(null);
  const cardRefs = useRef({});
  const [activePreviewIndex, setActivePreviewIndex] = useState(null);
  const previewImage =
    activePreviewIndex !== null ? performanceImages[activePreviewIndex]?.src : null;

  useGSAP(
    () => {
      const section = sectionRef.current;
      if (!section) return;

      const mm = gsap.matchMedia();
      mm.add("(min-width: 768px)", () => {
        const cards = performanceImgPositions
          .filter((item) => !item.static)
          .map((item) => cardRefs.current[item.id])
          .filter(Boolean);
        if (!cards.length) return;

        const resetCards = () => {
          gsap.set(cards, {
            clearProps: "x,y,opacity",
          });
        };

        resetCards();

        const tl = gsap.timeline({
          defaults: { ease: "none" },
          scrollTrigger: {
            trigger: section,
            start: "top 85%",
            end: "bottom 55%",
            scrub: 0.45,
            invalidateOnRefresh: true,
            fastScrollEnd: true,
          },
        });

        performanceImgPositions.forEach((item) => {
          if (item.static) return;
          const el = cardRefs.current[item.id];
          if (!el) return;

          const fromX = typeof item.left === "number" ? 180 : -180;
          const fromY = 26;

          tl.fromTo(
            el,
            {
              x: fromX,
              y: fromY,
              scale: 0.9,
              opacity: 1,
              force3D: true,
            },
            {
              x: 0,
              y: -28,
              scale: 1,
              opacity: 1,
              duration: 0.75,
              force3D: true,
              immediateRender: false,
            },
            0
          );
        });

        const rafId = requestAnimationFrame(() => ScrollTrigger.refresh());
        ScrollTrigger.addEventListener("refreshInit", resetCards);

        return () => {
          cancelAnimationFrame(rafId);
          ScrollTrigger.removeEventListener("refreshInit", resetCards);
          tl.kill();
        };
      });

      return () => mm.revert();
    },
    { scope: sectionRef }
  );

  useEffect(() => {
    const refresh = () => ScrollTrigger.refresh();
    const delayedRefresh = () => setTimeout(refresh, 80);

    if (document.readyState === "complete") {
      delayedRefresh();
    }

    window.addEventListener("load", delayedRefresh);
    window.addEventListener("resize", delayedRefresh);
    window.addEventListener("pageshow", delayedRefresh);

    return () => {
      window.removeEventListener("load", delayedRefresh);
      window.removeEventListener("resize", delayedRefresh);
      window.removeEventListener("pageshow", delayedRefresh);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setActivePreviewIndex(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <section
      id="performance"
      ref={sectionRef}
      className="hidden md:block relative w-full bg-[#000] overflow-hidden"
    >
      <div className="text-center text-[#351F72] mt-10">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
          Why Choose Us
        </h2>
        <p className="text-gray-600 text-sm sm:text-base">
          Affordable, Fast & Expert Solutions for Your Devices
        </p>
      </div>

      <div className="performance-container">
        <div className="image-stack relative w-full h-[420px] sm:h-[520px] lg:h-[700px]">
          {performanceImages.map((img, index) => {
            const item = performanceImgPositions.find((p) => p.id === img.id);
            const posStyle = item?.static
              ? { left: "50%", top: "50%", transform: "translate(-50%, -50%)" }
              : {
                  left: typeof item?.left === "number" ? `${item.left}%` : "auto",
                  right: typeof item?.right === "number" ? `${item.right}%` : "auto",
                  bottom: typeof item?.bottom === "number" ? `${item.bottom}%` : "auto",
                  transform: item?.transform || undefined,
                };

            return (
              <div
                key={img.id}
                className={`${img.id}`}
                ref={(node) => {
                  if (node) cardRefs.current[img.id] = node;
                }}
                style={{
                  position: "absolute",
                  width: img.id === "p5" ? 350 : 320,
                  borderRadius: 14,
                  overflow: "hidden",
                  boxShadow: "0 30px 90px rgba(0,0,0,0.35)",
                  cursor: "pointer",
                  ...posStyle,
                }}
                onClick={() => setActivePreviewIndex(index)}
                aria-label={`Open ${imageTitles[img.id] || img.id}`}
              >
                <img
                  src={img.src}
                  alt={img.id}
                  draggable={false}
                  onLoad={() => ScrollTrigger.refresh()}
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    bottom: 10,
                    padding: "6px 10px",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    textAlign: "center",
                    letterSpacing: "0.02em",
                    background: "rgba(10,15,30,0.55)",
                    borderRadius: 8,
                    backdropFilter: "blur(2px)",
                    transform: "translateX(-50%)",
                  }}
                >
                  {imageTitles[img.id] || "PC Care"}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {previewImage && (
        <div
          className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setActivePreviewIndex(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="absolute right-2 top-2 z-20 rounded-md px-3 py-1.5 text-sm font-semibold text-[#351F72]"
              onClick={() => setActivePreviewIndex(null)}
            >
              x
            </button>
            <img
              src={previewImage}
              alt="Preview"
              className="h-auto max-h-[86vh] w-full rounded-xl object-contain bg-white"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Aslider;
